import { useEffect, useMemo, useRef, useState } from 'react'
import { MRT_ColumnDef } from 'material-react-table'
import Add from '@mui/icons-material/Add'
import Delete from '@mui/icons-material/Delete'
import {
  Box,
  Button,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material'
import { useStytchIsAuthorized } from '@stytch/react/b2b'
import useOverlay from '@/hooks/useOverlay'
import useSizeObserver from '@/hooks/useSizeObserver'
import {
  useDeleteImporterEmail,
  useGetImporterEmails,
} from '@/service-library/hooks/importer-emails'
import { useGetProjects } from '@/service-library/hooks/projects'
import useAllOrganizations from '@/services/hooks/useAllOrganizations'
import { ImporterEmail } from '@/types/importer-emails'
import { showErrorSnackbar } from '@/utils/snackbars'
import useZerapixTable from '@/components/zerapix-table/useZerapixTable'
import ZerapixTable from '@/components/zerapix-table/ZerapixTable'
import DeleteImporterEmailDialog from '../project-settings/DeleteImporterEmailDialog'
import AddImporterEmailDialog from './AddImporterEmailDialog'

export default function UploadEmailAddresses() {
  const addEmailOverlay = useOverlay()
  const deleteEmailOverlay = useOverlay()
  const containerRef = useRef<HTMLDivElement>(null)
  const { height } = useSizeObserver({ containerRef })

  const { isAuthorized: canEditProjects } = useStytchIsAuthorized(
    'pd.project',
    'edit',
  )

  const { projects } = useGetProjects({
    filters: { fields__only: 'id,name', limit: '1000' },
  })

  const {
    importerEmails = [],
    isPending,
    queryKey,
  } = useGetImporterEmails({
    filters: { fields__include: 'org', limit: '1000' },
  })

  const { organizations, isPending: isFetchingAllOrgs } = useAllOrganizations()

  const [importerEmail, setImporterEmail] = useState<ImporterEmail>()

  const { deleteImporterEmail } = useDeleteImporterEmail({
    listQueryKey: queryKey,
    onError: () => {
      showErrorSnackbar('Unable to delete email. Please try again later.')
    },
  })

  function handleOnDelete() {
    importerEmail && deleteImporterEmail(importerEmail.id)
    deleteEmailOverlay.close()
  }

  useEffect(() => {
    if (!addEmailOverlay.isOpen && !deleteEmailOverlay.isOpen && importerEmail)
      setImporterEmail(undefined)
  }, [addEmailOverlay.isOpen, deleteEmailOverlay.isOpen, importerEmail])

  const columns: MRT_ColumnDef<ImporterEmail>[] = useMemo(
    () => [
      {
        header: 'Organization',
        accessorFn: (row) => {
          const organization = organizations.find((o) => o.id === row.org_id)
          return `${organization?.name} ${
            !organization?.parent_org_id ? '(Root)' : ''
          }`
        },
      },
      {
        header: 'Project',
        accessorFn: (row) => {
          const project = projects.find((p) => p.id === row.project_id)
          return project ? project.name : 'Auto Assign'
        },
      },
      {
        accessorKey: 'email',
        header: 'Email',
        size: 400,
        enableClickToCopy: true,
      },
    ],
    [organizations, projects],
  )

  const table = useZerapixTable<ImporterEmail>({
    title: (
      <Stack>
        <Typography variant="h6">Upload Email Addresses</Typography>
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ maxWidth: 700 }}
        >
          Forward emails to these email addresses to automatically process
          emails as documents. Each attachment will be uploaded as a separate
          document. The email body will be included with each attachment.
        </Typography>
      </Stack>
    ),
    columns,
    data: importerEmails,
    initialState: {
      density: 'comfortable',
    },
    enableColumnResizing: true,
    enableRowActions: true,
    enableStickyHeader: true,
    enableSorting: true,
    localization: {
      noRecordsToDisplay: 'No emails have been added.',
    },
    muiTableBodyRowProps: {
      hover: false,
    },
    muiTableContainerProps: {
      sx: { maxHeight: height - 100 },
    },
    renderRowActionMenuItems: canEditProjects
      ? ({ closeMenu, row }) => {
          return [
            <MenuItem
              key="delete"
              onClick={() => {
                deleteEmailOverlay.open()
                setImporterEmail(row.original)
                closeMenu()
              }}
            >
              <ListItemIcon>
                <Delete color="error" fontSize="small" />
              </ListItemIcon>
              <ListItemText
                slotProps={{
                  primary: { variant: 'inherit', color: 'error' },
                }}
              >
                Delete
              </ListItemText>
            </MenuItem>,
          ]
        }
      : undefined,
    renderTableActions: canEditProjects
      ? () => (
          <div style={{ flexShrink: 0 }}>
            <Button
              disabled={isFetchingAllOrgs}
              onClick={addEmailOverlay.open}
              startIcon={<Add />}
              variant="text"
            >
              New Email
            </Button>
          </div>
        )
      : undefined,
    state: {
      showSkeletons: isPending,
    },
  })

  return (
    <Box
      ref={containerRef}
      sx={{ height: '100%', overflow: 'auto', px: 2, pt: 2, minHeight: '0' }}
    >
      <ZerapixTable<ImporterEmail> table={table} />
      <AddImporterEmailDialog
        isFetchingOrgs={isFetchingAllOrgs}
        listQueryKey={queryKey}
        organizations={organizations}
        projects={projects}
        overlay={addEmailOverlay}
        importerEmails={importerEmails}
      />
      {importerEmail && (
        <DeleteImporterEmailDialog
          importerEmail={importerEmail}
          overlay={deleteEmailOverlay}
          onDelete={handleOnDelete}
        />
      )}
    </Box>
  )
}
